// @ts-nocheck
import styles from './Menu.module.scss';
import { useEffect, useMemo, useState } from 'react';
import { Menu as MenuComponent } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import {
    CalendarOutlined,
    FileOutlined,
    MonitorOutlined,
    SettingOutlined,
    UserOutlined,
    GlobalOutlined,
    ReloadOutlined,
    CopyrightOutlined,
    BankOutlined,
    GiftOutlined,
    ProfileOutlined,
} from '@ant-design/icons';
import { findMatchedMenu, MENUS } from '../../../../utils/routes';
import { useSelector } from 'react-redux';
import { PERMISSIONS } from '../../../../utils/constants';
import useAccess from '../../../../utils/hooks/useAccess';
import { generateNewBackOfficeUrl, NEW_BO_APP_REDIRECT_MAP } from '../../../../utils/new-bo-urls';

const { SubMenu } = MenuComponent;

const Menu = () => {
    const checkAccess = useAccess();
    const location = useLocation();

    const collapsed = useSelector(state => state.siderMenu.isSiderCollapsed);

    const [menuProps, setMenuProps] = useState({});
    const [openKeys, setOpenKeys] = useState([]);

    const selectedKeys = useMemo(() => {
        const matchedMenu = findMatchedMenu(location.pathname);

        if (matchedMenu) {
            const foundMenus = Object.values(MENUS)
                .filter(menu => {
                    return !!menu.subMenus?.find(m => m === matchedMenu.id);
                })
                .map(menu => menu.id.toString());

            return [matchedMenu.id.toString(), ...foundMenus];
        }

        return [];
    }, [location.pathname]);

    useEffect(() => {
        const matchedMenu = findMatchedMenu(location.pathname);

        if (matchedMenu) {
            const foundMenus = Object.values(MENUS)
                .filter(menu => {
                    return !!menu.subMenus?.find(m => m === matchedMenu.id);
                })
                .map(menu => menu.id.toString());

            if (foundMenus.length) {
                setOpenKeys(foundMenus);
            }
        }
    }, [location.pathname]);

    useEffect(() => {
        if (collapsed) setMenuProps({});
        else setMenuProps({ openKeys });
    }, [collapsed, openKeys]);

    const hasChildAccess = menu => menu.subMenus?.some(childId => checkAccess(childId, PERMISSIONS.view));

    const onSubMenuTitleClick = ({ key, domEvent }) => {
        let newOpenKeys = [...openKeys];

        if (openKeys.includes(key)) {
            newOpenKeys = newOpenKeys.filter(k => k !== key);
        } else {
            newOpenKeys.push(key);
        }

        setOpenKeys(newOpenKeys);
    };

    return (
        <MenuComponent className={styles.menu} theme='dark' mode='inline' selectedKeys={selectedKeys} {...menuProps}>
            {hasChildAccess(MENUS.eventsCreation) && (
                <SubMenu
                    key={MENUS.eventsCreation.id}
                    icon={<CalendarOutlined />}
                    title='Events Creation'
                    onTitleClick={onSubMenuTitleClick}
                >
                    {hasChildAccess(MENUS.templates, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.templates.id} className={styles.menuItem}>
                            <Link to={MENUS.templates.path}>{MENUS.templates.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {hasChildAccess(MENUS.weight, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.weight.id} className={styles.menuItem}>
                            <Link to={MENUS.weight.path}>{MENUS.weight.name}</Link>
                        </MenuComponent.Item>
                    )}
                </SubMenu>
            )}
            {hasChildAccess(MENUS.monitoring) && (
                <SubMenu
                    key={MENUS.monitoring.id}
                    icon={<MonitorOutlined />}
                    title='Monitoring'
                    onTitleClick={onSubMenuTitleClick}
                >
                    {checkAccess(MENUS.preMatch.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.preMatch.id} className={styles.menuItem}>
                            <Link to={MENUS.preMatch.path}>{MENUS.preMatch.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.live.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.live.id} className={styles.menuItem}>
                            <Link to={MENUS.live.path}>{MENUS.live.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.betsMonitor.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.betsMonitor.id} className={styles.menuItem}>
                            <Link to={MENUS.betsMonitor.path}>{MENUS.betsMonitor.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {hasChildAccess(MENUS.settlement) && (
                        <MenuComponent.Item key={MENUS.settlement.id} className={styles.menuItem}>
                            <Link to={MENUS.settlement.path}>{MENUS.settlement.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {hasChildAccess(MENUS.cashoutBlock) && (
                        <MenuComponent.Item key={MENUS.cashoutBlock.id} className={styles.menuItem}>
                            <Link to={MENUS.cashoutBlock.path}>{MENUS.cashoutBlock.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.sportsPerformance.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.sportsPerformance.id} className={styles.menuItem}>
                            <Link to={MENUS.sportsPerformance.path}>{MENUS.sportsPerformance.name}</Link>
                        </MenuComponent.Item>
                    )}
                </SubMenu>
            )}
            {hasChildAccess(MENUS.reports) && (
                <SubMenu
                    key={MENUS.reports.id}
                    icon={<FileOutlined />}
                    title='Reports'
                    onTitleClick={onSubMenuTitleClick}
                >
                    {checkAccess(MENUS.quickSearch.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.quickSearch.id} className={styles.menuItem}>
                            <Link to={MENUS.quickSearch.path}>{MENUS.quickSearch.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.betHistory.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.betHistory.id} className={styles.menuItem}>
                            <Link to={generateNewBackOfficeUrl(NEW_BO_APP_REDIRECT_MAP[MENUS.betHistory.path])}>
                                {MENUS.betHistory.name}
                            </Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.gamingActivity.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.gamingActivity.id} className={styles.menuItem}>
                            <Link to={generateNewBackOfficeUrl(NEW_BO_APP_REDIRECT_MAP[MENUS.gamingActivity.path])}>
                                {MENUS.gamingActivity.name}
                            </Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.loginsHistory.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.loginsHistory.id} className={styles.menuItem}>
                            <Link to={MENUS.loginsHistory.path}>{MENUS.loginsHistory.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.accountingReport.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.accountingReport.id} className={styles.menuItem}>
                            <Link to={MENUS.accountingReport.path}>{MENUS.accountingReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {hasChildAccess(MENUS.taxReport) && (
                        <MenuComponent.Item key={MENUS.taxReport.id} className={styles.menuItem}>
                            <Link to={MENUS.taxReport.path}>{MENUS.taxReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.onlinePlayersReport.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.onlinePlayersReport.id} className={styles.menuItem}>
                            <Link to={MENUS.onlinePlayersReport.path}>{MENUS.onlinePlayersReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.declinedBetsHistory.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.declinedBetsHistory.id} className={styles.menuItem}>
                            <Link to={MENUS.declinedBetsHistory.path}>{MENUS.declinedBetsHistory.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.netCashReport.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.netCashReport.id} className={styles.menuItem}>
                            <Link to={MENUS.netCashReport.path}>{MENUS.netCashReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.registrationsHistory.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.registrationsHistory.id} className={styles.menuItem}>
                            <Link to={MENUS.registrationsHistory.path}>{MENUS.registrationsHistory.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.frozenAccountsReport.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.frozenAccountsReport.id} className={styles.menuItem}>
                            <Link to={MENUS.frozenAccountsReport.path}>{MENUS.frozenAccountsReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.inactivePlayersReport.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.inactivePlayersReport.id} className={styles.menuItem}>
                            <Link to={MENUS.inactivePlayersReport.path}>{MENUS.inactivePlayersReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.auditTrailReport.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.auditTrailReport.id} className={styles.menuItem}>
                            <Link to={MENUS.auditTrailReport.path}>{MENUS.auditTrailReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.ticketOperationsHistory.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.ticketOperationsHistory.id} className={styles.menuItem}>
                            <Link to={MENUS.ticketOperationsHistory.path}>{MENUS.ticketOperationsHistory.name}</Link>
                        </MenuComponent.Item>
                    )}
                </SubMenu>
            )}
            {hasChildAccess(MENUS.accounts) && (
                <SubMenu
                    key={MENUS.accounts.id}
                    icon={<UserOutlined />}
                    title='Accounts'
                    onTitleClick={onSubMenuTitleClick}
                >
                    {checkAccess(MENUS.users.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.users.id} className={styles.menuItem}>
                            <Link to={generateNewBackOfficeUrl(NEW_BO_APP_REDIRECT_MAP[MENUS.users.path])}>
                                {MENUS.users.name}
                            </Link>
                        </MenuComponent.Item>
                    )}
                </SubMenu>
            )}
            {hasChildAccess(MENUS.configurations) && (
                <SubMenu
                    key={MENUS.configurations.id}
                    icon={<SettingOutlined />}
                    title='Configurations'
                    onTitleClick={onSubMenuTitleClick}
                >
                    {checkAccess(MENUS.messageList.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.messageList.id} className={styles.menuItem}>
                            <Link to={MENUS.messageList.path}>{MENUS.messageList.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.menuManagement.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.menuManagement.id} className={styles.menuItem}>
                            <Link to={MENUS.menuManagement.path}>{MENUS.menuManagement.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {hasChildAccess(MENUS.emails) && (
                        <MenuComponent.Item key={MENUS.emails.id} className={styles.menuItem}>
                            <Link to={MENUS.emails.path}>{MENUS.emails.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.paymentMethods.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.paymentMethods.id} className={styles.menuItem}>
                            <Link to={MENUS.paymentMethods.path}>{MENUS.paymentMethods.name}</Link>
                        </MenuComponent.Item>
                    )}
                </SubMenu>
            )}
            {hasChildAccess(MENUS.translations) && (
                <SubMenu
                    key={MENUS.translations.id}
                    icon={<GlobalOutlined />}
                    title='Translations'
                    onTitleClick={onSubMenuTitleClick}
                >
                    {checkAccess(MENUS.serverErrorsOnline.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.serverErrorsOnline.id} className={styles.menuItem}>
                            <Link to={MENUS.serverErrorsOnline.path}>{MENUS.serverErrorsOnline.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.userInterfaceOnline.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.userInterfaceOnline.id} className={styles.menuItem}>
                            <Link to={MENUS.userInterfaceOnline.path}>{MENUS.userInterfaceOnline.name}</Link>
                        </MenuComponent.Item>
                    )}
                </SubMenu>
            )}
            {hasChildAccess(MENUS.cms) && (
                <SubMenu
                    key={MENUS.cms.id}
                    icon={<CopyrightOutlined />}
                    title={MENUS.cms.name}
                    onTitleClick={onSubMenuTitleClick}
                >
                    {hasChildAccess(MENUS.banners) && (
                        <MenuComponent.Item key={MENUS.banners.id} className={styles.menuItem}>
                            <Link to={MENUS.banners.path}>{MENUS.banners.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.featuredLinks.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.featuredLinks.id} className={styles.menuItem}>
                            <Link to={MENUS.featuredLinks.path}>{MENUS.featuredLinks.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.featured.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.featured.id} className={styles.menuItem}>
                            <Link to={MENUS.featured.path}>{MENUS.featured.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.bettingRules.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.bettingRules.id} className={styles.menuItem}>
                            <Link to={MENUS.bettingRules.path}>{MENUS.bettingRules.name}</Link>
                        </MenuComponent.Item>
                    )}
                </SubMenu>
            )}
            {hasChildAccess(MENUS.bank) && (
                <SubMenu
                    key={MENUS.bank.id}
                    icon={<BankOutlined />}
                    title={MENUS.bank.name}
                    onTitleClick={onSubMenuTitleClick}
                >
                    {checkAccess(MENUS.depositsWithdrawals.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.depositsWithdrawals.id} className={styles.menuItem}>
                            <Link to={MENUS.depositsWithdrawals.path}>{MENUS.depositsWithdrawals.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.withdrawalAutoprocessing.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.withdrawalAutoprocessing.id} className={styles.menuItem}>
                            <Link to={MENUS.withdrawalAutoprocessing.path}>{MENUS.withdrawalAutoprocessing.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.moneyTransactions.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.moneyTransactions.id} className={styles.menuItem}>
                            <Link to={MENUS.moneyTransactions.path}>{MENUS.moneyTransactions.name}</Link>
                        </MenuComponent.Item>
                    )}
                </SubMenu>
            )}
            {hasChildAccess(MENUS.bonus) && (
                <SubMenu
                    key={MENUS.bonus.id}
                    icon={<GiftOutlined />}
                    title={MENUS.bonus.name}
                    onTitleClick={onSubMenuTitleClick}
                >
                    {checkAccess(MENUS.bonuses.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.bonuses.id} className={styles.menuItem}>
                            <Link to={MENUS.bonuses.path}>{MENUS.bonuses.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.bonusEngineMonitor.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.bonusEngineMonitor.id} className={styles.menuItem}>
                            <Link to={MENUS.bonusEngineMonitor.path}>{MENUS.bonusEngineMonitor.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.bonusWageringReport.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.bonusWageringReport.id} className={styles.menuItem}>
                            <Link to={MENUS.bonusWageringReport.path}>{MENUS.bonusWageringReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.promotions.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.promotions.id} className={styles.menuItem}>
                            <Link to={MENUS.promotions.path}>{MENUS.promotions.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.grandMassBonuses.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.grandMassBonuses.id} className={styles.menuItem}>
                            <Link to={MENUS.grandMassBonuses.path}>{MENUS.grandMassBonuses.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.playerBonusesReport.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.playerBonusesReport.id} className={styles.menuItem}>
                            <Link to={MENUS.playerBonusesReport.path}>{MENUS.playerBonusesReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                </SubMenu>
            )}
            {hasChildAccess(MENUS.player) && (
                <SubMenu
                    key={MENUS.player.id}
                    icon={<ProfileOutlined />}
                    title={MENUS.player.name}
                    onTitleClick={onSubMenuTitleClick}
                >
                    {checkAccess(MENUS.playerSearch.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.playerSearch.id} className={styles.menuItem}>
                            <Link to={MENUS.playerSearch.path}>{MENUS.playerSearch.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.playerMassUpdate.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.playerMassUpdate.id} className={styles.menuItem}>
                            <Link to={MENUS.playerMassUpdate.path}>{MENUS.playerMassUpdate.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.playerActivityReport.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.playerActivityReport.id} className={styles.menuItem}>
                            <Link to={MENUS.playerActivityReport.path}>{MENUS.playerActivityReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.playerSegmentation.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.playerSegmentation.id} className={styles.menuItem}>
                            <Link to={MENUS.playerSegmentation.path}>{MENUS.playerSegmentation.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.playerSegmentationReport.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.playerSegmentationReport.id} className={styles.menuItem}>
                            <Link to={MENUS.playerSegmentationReport.path}>{MENUS.playerSegmentationReport.name}</Link>
                        </MenuComponent.Item>
                    )}
                    {checkAccess(MENUS.bulkSegmentUpdate.id, PERMISSIONS.view) && (
                        <MenuComponent.Item key={MENUS.bulkSegmentUpdate.id} className={styles.menuItem}>
                            <Link to={MENUS.bulkSegmentUpdate.path}>{MENUS.bulkSegmentUpdate.name}</Link>
                        </MenuComponent.Item>
                    )}
                </SubMenu>
            )}
            {/*{!isProd && (*/}
            {/*    <SubMenu key={'player'} icon={<UserOutlined />} title='Player' onTitleClick={onSubMenuTitleClick}>*/}
            {/*        <MenuComponent.Item key={PLAYER_SUBMENUS.playerDetails.id} className={styles.menuItem}>*/}
            {/*            <Link to={PLAYER_SUBMENUS.playerDetails.path}>{PLAYER_SUBMENUS.playerDetails.name}</Link>*/}
            {/*        </MenuComponent.Item>*/}
            {/*        <MenuComponent.Item key={PERMISSIONS_MENU.id} className={styles.menuItem}>*/}
            {/*            <Link to={PERMISSIONS_MENU.path}>{PERMISSIONS_MENU.name}</Link>*/}
            {/*        </MenuComponent.Item>*/}
            {/*    </SubMenu>*/}
            {/*)}*/}
            {checkAccess(MENUS.updateVersion.id, PERMISSIONS.view) && (
                <MenuComponent.Item icon={<ReloadOutlined />} key={MENUS.updateVersion.id}>
                    <Link to={MENUS.updateVersion.path}>{MENUS.updateVersion.name}</Link>
                </MenuComponent.Item>
            )}
        </MenuComponent>
    );
};

export default Menu;
